import React, { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import AppLayout from '../../components/AppLayout';
import PageHeader from '../../components/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { MaskedInput } from '../../components/ui/masked-input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import usePageTitle from '../../utils/usePageTitle';
import { brazilianStates, brazilianCities } from '../../utils/brazilianLocations';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';

function TenantSettings() {
    usePageTitle('Configurações da Conta');
    const { toast, toastApiError } = useToast();
    const { can } = useAuth();
    const canTenant = can('Tenant');
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        is_active: true,
        // Company information
        company_name: '',
        company_cnpj: '',
        company_street: '',
        company_number: '',
        company_complement: '',
        company_city: '',
        company_state: '',
        company_zip_code: '',
        // Representative information
        representative_name: '',
        representative_cpf: '',
        representative_nationality: 'brasileiro(a)',
        representative_marital_status: 'solteiro(a)',
        representative_rg: '',
        representative_rg_issuer: '',
        representative_street: '',
        representative_number: '',
        representative_complement: '',
        representative_city: '',
        representative_state: '',
        representative_zip_code: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tenantId, setTenantId] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const [hasLogo, setHasLogo] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const logoInputRef = useRef(null);

    useEffect(() => {
        loadTenant();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return () => {
            if (logoUrl) window.URL.revokeObjectURL(logoUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadLogo = async (id) => {
        try {
            const response = await api.get(`/tenants/${id}/logo`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(response.data);
            setLogoUrl((prev) => {
                if (prev) window.URL.revokeObjectURL(prev);
                return url;
            });
            setHasLogo(true);
        } catch (error) {
            setHasLogo(false);
            setLogoUrl((prev) => {
                if (prev) window.URL.revokeObjectURL(prev);
                return null;
            });
        }
    };

    const handleLogoSelect = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !tenantId) return;

        setLogoUploading(true);
        try {
            const formData = new FormData();
            formData.append('logo', file);
            await api.post(`/tenants/${tenantId}/logo`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            await loadLogo(tenantId);
            toast({ title: 'Logo salva', description: 'A logo será usada no cabeçalho dos PDFs', variant: 'success' });
        } catch (error) {
            console.error('Error uploading logo:', error);
            toastApiError(error, { title: 'Erro ao enviar', fallback: 'Não foi possível enviar a logo' });
        } finally {
            setLogoUploading(false);
            if (logoInputRef.current) logoInputRef.current.value = '';
        }
    };

    const handleLogoRemove = async () => {
        if (!tenantId) return;
        setLogoUploading(true);
        try {
            await api.delete(`/tenants/${tenantId}/logo`);
            setHasLogo(false);
            setLogoUrl((prev) => {
                if (prev) window.URL.revokeObjectURL(prev);
                return null;
            });
            toast({ title: 'Logo removida', variant: 'success' });
        } catch (error) {
            console.error('Error removing logo:', error);
            toastApiError(error, { title: 'Erro ao remover', fallback: 'Não foi possível remover a logo' });
        } finally {
            setLogoUploading(false);
        }
    };

    const loadTenant = async () => {
        try {
            // Pega o tenant do usuário logado
            const response = await api.get('/me');
            const currentTenant = response.data.current_tenant;

            if (currentTenant) {
                setTenantId(currentTenant.id);
                setFormData({
                    name: currentTenant.name || '',
                    email: currentTenant.email || '',
                    phone: currentTenant.phone || '',
                    is_active: currentTenant.is_active !== undefined ? currentTenant.is_active : true,
                    // Company information
                    company_name: currentTenant.company_name || '',
                    company_cnpj: currentTenant.company_cnpj || '',
                    company_street: currentTenant.company_street || '',
                    company_number: currentTenant.company_number || '',
                    company_complement: currentTenant.company_complement || '',
                    company_city: currentTenant.company_city || '',
                    company_state: currentTenant.company_state || '',
                    company_zip_code: currentTenant.company_zip_code || '',
                    // Representative information
                    representative_name: currentTenant.representative_name || '',
                    representative_cpf: currentTenant.representative_cpf || '',
                    representative_nationality: currentTenant.representative_nationality || 'brasileiro(a)',
                    representative_marital_status: currentTenant.representative_marital_status || 'solteiro(a)',
                    representative_rg: currentTenant.representative_rg || '',
                    representative_rg_issuer: currentTenant.representative_rg_issuer || '',
                    representative_street: currentTenant.representative_street || '',
                    representative_number: currentTenant.representative_number || '',
                    representative_complement: currentTenant.representative_complement || '',
                    representative_city: currentTenant.representative_city || '',
                    representative_state: currentTenant.representative_state || '',
                    representative_zip_code: currentTenant.representative_zip_code || '',
                });
                loadLogo(currentTenant.id);
            }
        } catch (error) {
            console.error('Error loading tenant:', error);
            toast({
                title: 'Erro ao carregar',
                description: 'Não foi possível carregar os dados da empresa',
                variant: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        // Se mudou o estado da empresa, limpa a cidade da empresa
        if (name === 'company_state') {
            setFormData({
                ...formData,
                [name]: value,
                company_city: '',
            });
        }
        // Se mudou o estado do representante, limpa a cidade do representante
        else if (name === 'representative_state') {
            setFormData({
                ...formData,
                [name]: value,
                representative_city: '',
            });
        }
        else {
            setFormData({
                ...formData,
                [name]: type === 'checkbox' ? checked : value,
            });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            await api.put(`/tenants/${tenantId}`, formData);
            toast({
                title: 'Empresa salva',
                description: 'Dados atualizados com sucesso',
                variant: 'success',
            });
        } catch (error) {
            console.error('Error saving tenant:', error);
            toastApiError(error, { title: 'Erro ao salvar', fallback: 'Não foi possível salvar os dados da empresa' });
        } finally {
            setSaving(false);
        }
    };

    return (
        <AppLayout>
            <div className="space-y-6">
                <PageHeader
                    title="Empresa"
                    breadcrumbs={[
                        { label: 'Dashboard', path: '/dashboard' },
                        { label: 'Configurações' },
                    ]}
                    actions={[
                        {
                            label: saving ? 'Salvando...' : 'Salvar',
                            icon: 'save',
                            type: 'submit',
                            form: 'tenant-form',
                            disabled: saving || loading || !canTenant,
                        },
                    ]}
                />

                {/* Logo */}
                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle>Logo da Empresa</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center gap-4">
                            <div className="w-32 h-32 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
                                {hasLogo && logoUrl ? (
                                    <img src={logoUrl} alt="Logo da empresa" className="max-w-full max-h-full object-contain" />
                                ) : (
                                    <span className="text-xs text-gray-400 text-center px-2">Sem logo</span>
                                )}
                            </div>
                            <div className="space-y-2">
                                <p className="text-sm text-gray-600">
                                    Usada no cabeçalho dos PDFs de pedido. Formatos aceitos: JPG, PNG ou WEBP (máx. 2MB).
                                </p>
                                <div className="flex gap-2">
                                    <input
                                        ref={logoInputRef}
                                        type="file"
                                        accept="image/png,image/jpeg,image/webp"
                                        onChange={handleLogoSelect}
                                        className="hidden"
                                        id="logo-input"
                                    />
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => logoInputRef.current?.click()}
                                        disabled={logoUploading || !canTenant}
                                    >
                                        {logoUploading ? 'Enviando...' : (hasLogo ? 'Trocar logo' : 'Enviar logo')}
                                    </Button>
                                    {hasLogo && (
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            onClick={handleLogoRemove}
                                            disabled={logoUploading || !canTenant}
                                        >
                                            Remover
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Form */}
                <form id="tenant-form" onSubmit={handleSubmit} autoComplete="off">
                    {/* Informações da Empresa */}
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>Informações da Empresa</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nome da Empresa *
                                    </label>
                                    <Input
                                        name="company_name"
                                        value={formData.company_name}
                                        onChange={handleChange}
                                        placeholder="Nome da empresa"
                                        required
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nome (Sistema)
                                    </label>
                                    <Input
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        placeholder="Nome usado no sistema"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        CNPJ *
                                    </label>
                                    <MaskedInput
                                        mask="cnpj"
                                        name="company_cnpj"
                                        value={formData.company_cnpj}
                                        onChange={handleChange}
                                        placeholder="00.000.000/0000-00"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Telefone
                                    </label>
                                    <MaskedInput
                                        mask="phone"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        placeholder="(00) 00000-0000"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        E-mail
                                    </label>
                                    <Input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        placeholder="contato@empresa.com.br"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        CEP
                                    </label>
                                    <MaskedInput
                                        mask="cep"
                                        name="company_zip_code"
                                        value={formData.company_zip_code}
                                        onChange={handleChange}
                                        placeholder="00000-000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Estado
                                    </label>
                                    <select
                                        name="company_state"
                                        value={formData.company_state}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">Selecione...</option>
                                        {brazilianStates.map((state) => (
                                            <option key={state.code} value={state.code}>
                                                {state.code} - {state.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cidade
                                    </label>
                                    <select
                                        name="company_city"
                                        value={formData.company_city}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        disabled={!formData.company_state}
                                    >
                                        <option value="">Selecione...</option>
                                        {formData.company_state && brazilianCities[formData.company_state]?.map((city) => (
                                            <option key={city} value={city}>
                                                {city}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Rua
                                    </label>
                                    <Input
                                        name="company_street"
                                        value={formData.company_street}
                                        onChange={handleChange}
                                        placeholder="Nome da rua"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Número
                                    </label>
                                    <Input
                                        name="company_number"
                                        value={formData.company_number}
                                        onChange={handleChange}
                                        placeholder="Nº"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Complemento
                                    </label>
                                    <Input
                                        name="company_complement"
                                        value={formData.company_complement}
                                        onChange={handleChange}
                                        placeholder="Apto, Sala, etc"
                                    />
                                </div>

                                <div className="md:col-span-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="is_active"
                                            name="is_active"
                                            checked={formData.is_active}
                                            onChange={handleChange}
                                            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                        />
                                        <label htmlFor="is_active" className="text-sm font-medium text-gray-700">
                                            Empresa ativa
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Dados do Representante */}
                    <Card className="mb-6">
                        <CardHeader>
                            <CardTitle>Dados do Representante Legal</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nome Completo *
                                    </label>
                                    <Input
                                        name="representative_name"
                                        value={formData.representative_name}
                                        onChange={handleChange}
                                        placeholder="Nome completo do representante"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        CPF *
                                    </label>
                                    <MaskedInput
                                        mask="cpf"
                                        name="representative_cpf"
                                        value={formData.representative_cpf}
                                        onChange={handleChange}
                                        placeholder="000.000.000-00"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nacionalidade
                                    </label>
                                    <Input
                                        name="representative_nationality"
                                        value={formData.representative_nationality}
                                        onChange={handleChange}
                                        placeholder="Ex: brasileiro(a)"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Estado Civil
                                    </label>
                                    <select
                                        name="representative_marital_status"
                                        value={formData.representative_marital_status}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="solteiro(a)">Solteiro(a)</option>
                                        <option value="casado(a)">Casado(a)</option>
                                        <option value="divorciado(a)">Divorciado(a)</option>
                                        <option value="viúvo(a)">Viúvo(a)</option>
                                        <option value="separado(a)">Separado(a)</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        RG
                                    </label>
                                    <Input
                                        name="representative_rg"
                                        value={formData.representative_rg}
                                        onChange={handleChange}
                                        placeholder="Número do RG"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Órgão Emissor
                                    </label>
                                    <Input
                                        name="representative_rg_issuer"
                                        value={formData.representative_rg_issuer}
                                        onChange={handleChange}
                                        placeholder="Ex: SSP/SC"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        CEP
                                    </label>
                                    <MaskedInput
                                        mask="cep"
                                        name="representative_zip_code"
                                        value={formData.representative_zip_code}
                                        onChange={handleChange}
                                        placeholder="00000-000"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Estado
                                    </label>
                                    <select
                                        name="representative_state"
                                        value={formData.representative_state}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    >
                                        <option value="">Selecione...</option>
                                        {brazilianStates.map((state) => (
                                            <option key={state.code} value={state.code}>
                                                {state.code} - {state.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Cidade
                                    </label>
                                    <select
                                        name="representative_city"
                                        value={formData.representative_city}
                                        onChange={handleChange}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        disabled={!formData.representative_state}
                                    >
                                        <option value="">Selecione...</option>
                                        {formData.representative_state && brazilianCities[formData.representative_state]?.map((city) => (
                                            <option key={city} value={city}>
                                                {city}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Rua
                                    </label>
                                    <Input
                                        name="representative_street"
                                        value={formData.representative_street}
                                        onChange={handleChange}
                                        placeholder="Nome da rua"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Número
                                    </label>
                                    <Input
                                        name="representative_number"
                                        value={formData.representative_number}
                                        onChange={handleChange}
                                        placeholder="Nº"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Complemento
                                    </label>
                                    <Input
                                        name="representative_complement"
                                        value={formData.representative_complement}
                                        onChange={handleChange}
                                        placeholder="Apto, Casa, etc"
                                    />
                                </div>
                            </div>
                        </CardContent>
                    </Card>


                </form>
            </div>
        </AppLayout>
    );
}

export default TenantSettings;

